import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Pagination,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import Autocomplete from "@mui/material/Autocomplete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTheme } from "@mui/material/styles";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useAuth } from "../../context/AuthContext";
import useFetchObjects from "../../api/useFetchObjects";
import { Download } from "@mui/icons-material";
import { exportComprehensiveSaleReportToPDF } from "../../utils/exportToPDF";
import { exportComprehensiveSaleReportToExcel } from "../../utils/exportSalesReport";

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const ALL_CUSTOMERS_OPTION = { id: "", name: "All Customers" };
const SALE_TYPE_OPTIONS = [
  { value: "all", label: "All Sales" },
  { value: "wholesale", label: "Wholesale" },
  { value: "retail", label: "Retail" },
  // { value: "mixed", label: "Mixed" },
];

const parseAmountValue = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "object" && value !== null) {
    const currencies = Object.keys(value);
    if (currencies.length > 0) {
      return parseFloat(value[currencies[0]]) || 0;
    }
  }
  const numStr = String(value).replace(/[^0-9.-]/g, "");
  return parseFloat(numStr) || 0;
};

const getSaleType = (sale) => {
  const items = Array.isArray(sale?.sale_items) ? sale.sale_items : [];
  if (!items.length) return "retail";

  const types = new Set(
    items.map((item) => {
      const salePrice = parseAmountValue(item?.sale_price);
      const wholesalePrice = parseAmountValue(item?.product_details?.wholesale_price);
      const retailPrice = parseAmountValue(
        item?.product_details?.retail_price ?? item?.product_details?.sale_price
      );

      if (wholesalePrice > 0 && salePrice === wholesalePrice) return "wholesale";
      if (retailPrice > 0 && salePrice === retailPrice) return "retail";
      if (wholesalePrice > 0 && retailPrice <= 0) return "wholesale";
      return "retail";
    })
  );

  return types.size === 1 ? Array.from(types)[0] : "mixed";
};

const formatReportDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
};

const normalizeList = (data) => (Array.isArray(data) ? data : data?.results || []);

const getOptionTitle = (options, value) => {
  const selected = options.find((option) => String(option.id) === String(value));
  return selected?.title || selected?.name || value || "-";
};

const parseStoredAmount = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/,/g, "").replace(/[^0-9.-]/g, "");
  return Number(cleaned) || 0;
};

const readStoredAgentReportRows = (agents = [], _refreshTick = 0) => {
  if (typeof window === "undefined") return [];

  const rows = [];
  const agentMap = new Map(agents.map((agent) => [String(agent.id), agent]));

  Object.keys(window.localStorage)
    .filter((key) => key.startsWith("agent-items-"))
    .forEach((key) => {
      const agentId = key.replace("agent-items-", "");
      const agent = agentMap.get(String(agentId));
      const agentType = String(agent?.agent_type || "").toLowerCase();
      if (!agent || agentType !== "customer") return;
      let parsed = [];

      try {
        const raw = window.localStorage.getItem(key);
        const maybeData = JSON.parse(raw);
        parsed = Array.isArray(maybeData) ? maybeData : [];
      } catch {
        parsed = [];
      }

      parsed.forEach((item, index) => {
        const total = parseStoredAmount(item?.total);
        const paid = parseStoredAmount(item?.paid);
        rows.push({
          id: `${agentId}-${item?.billNo || index}`,
          agentId,
          agentType: "customer",
          billNo: item?.billNo || "-",
          date: item?.date || "-",
          description: item?.description || "-",
          unit: item?.unit || "-",
          qty: item?.qty || 0,
          currency: item?.currency || "-",
          total,
          paid,
          remaining: total - paid,
        });
      });
    });

  return rows.sort((a, b) => {
    const aTime = new Date(a.date).getTime();
    const bTime = new Date(b.date).getTime();
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return bTime - aTime;
  });
};

const ComprehensiveSaleReport = ({
  selectedWarehouse = "",
  setSelectedWarehouse = () => {},
}) => {
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === "dark";
  const auth = useAuth();
  const token = auth?.user?.token;

  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSaleType, setSelectedSaleType] = useState("all");
  const [viewMode, setViewMode] = useState("simple");
  const [customerReportPage, setCustomerReportPage] = useState(0);
  const [customerReportRowsPerPage, setCustomerReportRowsPerPage] = useState(10);
  const [descriptionAnchorEl, setDescriptionAnchorEl] = useState(null);
  const [activeDescription, setActiveDescription] = useState("");
  const [customerReportRefreshTick, setCustomerReportRefreshTick] = useState(0);

  const queryString = useMemo(() => {
    const params = ["page_size=all"]; // Always fetch all records for reports
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedWarehouse) params.push(`shop=${selectedWarehouse}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    return `?${params.join("&")}`;
  }, [startDate, endDate, selectedWarehouse, selectedCustomer]);

  const { data: salesData, isLoading: salesLoading } = useFetchObjects(
    ["sales", queryString],
    `shop/sales/${queryString}`,
    token
  );
  const { data: paymentsData, isLoading: paymentsLoading } = useFetchObjects(
    ["sale-payments", queryString],
    `shop/sale_payments/${queryString}`,
    token
  );
  const { data: returnsData, isLoading: returnsLoading } = useFetchObjects(
    ["sale-returns", queryString],
    `shop/sale-returns/${queryString}`,
    token
  );
  const { data: balancePaymentsData, isLoading: balancePaymentsLoading } =
    useFetchObjects(
      ["customer-balance-payments", queryString],
      `shop/customer-balance-payments/${queryString}`,
      token
    );
  const { data: customersData } = useFetchObjects(
    ["customers"],
    `shop/customers?page_size=all`,
    token
  );
  const { data: unitsData } = useFetchObjects(
    ["report-units"],
    "shop/units/?page_size=all",
    token
  );
  const { data: currenciesData } = useFetchObjects(
    ["report-currencies"],
    "shop/currencies/?page_size=all",
    token
  );
  const { data: branchesData } = useFetchObjects(
    ["report-branches"],
    "shop/shop/?page_size=1000",
    token
  );

  const sales = salesData?.results || salesData || [];
  const payments = paymentsData?.results || paymentsData || [];
  const returns = returnsData?.results || returnsData || [];
  const balancePayments =
    balancePaymentsData?.results || balancePaymentsData || [];
  const customers = useMemo(
    () =>
      (customersData?.results || customersData || []).filter(
        (customer) =>
          String(customer?.agent_type || "").toLowerCase() === "customer"
      ),
    [customersData]
  );
  const units = useMemo(() => normalizeList(unitsData), [unitsData]);
  const currencies = useMemo(
    () => normalizeList(currenciesData),
    [currenciesData]
  );
  const branches = branchesData?.results || branchesData || [];
  const customerOptions = useMemo(
    () => [ALL_CUSTOMERS_OPTION, ...customers],
    [customers]
  );

  const selectedCustomerObj = useMemo(() => {
    if (!selectedCustomer) return null;
    return customers.find((c) => c.id == selectedCustomer) || null;
  }, [customers, selectedCustomer]);
  const selectedCustomerOption = useMemo(() => {
    return (
      customerOptions.find(
        (customer) => String(customer.id) === String(selectedCustomer)
      ) || ALL_CUSTOMERS_OPTION
    );
  }, [customerOptions, selectedCustomer]);

  const balancePaymentsFiltered = useMemo(() => {
    if (!selectedCustomer) return balancePayments;
    return balancePayments.filter(
      (bp) => bp.customer_details?.id == selectedCustomer
    );
  }, [balancePayments, selectedCustomer]);

  const customerReportRows = useMemo(() => {
    return readStoredAgentReportRows(customers, customerReportRefreshTick);
  }, [customers, customerReportRefreshTick]);

  const filteredCustomerReportRows = useMemo(() => {
    const start = startDate ? startDate.startOf("day") : null;
    const end = endDate ? endDate.endOf("day") : null;

    return customerReportRows.filter((row) => {
      if (selectedCustomer && String(row.agentId) !== String(selectedCustomer)) {
        return false;
      }

      if (!start && !end) return true;

      const rowDate = row.date ? new Date(row.date) : null;
      if (!rowDate || Number.isNaN(rowDate.getTime())) return false;

      if (start && rowDate < start.toDate()) return false;
      if (end && rowDate > end.toDate()) return false;
      return true;
    });
  }, [customerReportRows, endDate, selectedCustomer, startDate]);

  const pagedCustomerReportRows = useMemo(() => {
    if (customerReportRowsPerPage === -1) return filteredCustomerReportRows;
    const start = customerReportPage * customerReportRowsPerPage;
    return filteredCustomerReportRows.slice(start, start + customerReportRowsPerPage);
  }, [customerReportPage, customerReportRowsPerPage, filteredCustomerReportRows]);

  const customerReportSummary = useMemo(() => {
    const totalQty = filteredCustomerReportRows.reduce(
      (sum, row) => sum + parseStoredAmount(row.qty),
      0
    );
    const totalAmount = filteredCustomerReportRows.reduce(
      (sum, row) => sum + parseStoredAmount(row.total),
      0
    );
    const totalPaid = filteredCustomerReportRows.reduce(
      (sum, row) => sum + parseStoredAmount(row.paid),
      0
    );
    return {
      records: filteredCustomerReportRows.length,
      totalQty,
      totalAmount,
      totalPaid,
      remaining: totalAmount - totalPaid,
    };
  }, [filteredCustomerReportRows]);

  useEffect(() => {
    setCustomerReportPage(0);
  }, [startDate, endDate, selectedCustomer]);

  const isLoading =
    salesLoading || paymentsLoading || returnsLoading || balancePaymentsLoading;

  const parseAmount = parseAmountValue;
  const getSaleSubtotal = (sale) => parseAmount(sale?.total_amount ?? 0);
  const getSaleDiscount = (sale) => parseAmount(sale?.discount ?? 0);
  // Sale total after discount (use effective_total_amount; fallback to total_amount)
  const getSaleTotal = (sale) =>
    parseAmount(sale?.effective_total_amount ?? sale?.total_amount ?? 0);

  // Filter sales by selected customer only
  const customerFilteredSales = useMemo(() => {
    if (!selectedCustomer) return sales;
    return sales.filter((sale) => sale.customer_details?.id == selectedCustomer);
  }, [sales, selectedCustomer]);

  const filteredSales = useMemo(() => {
    if (selectedSaleType === "all") return customerFilteredSales;
    return customerFilteredSales.filter(
      (sale) => getSaleType(sale) === selectedSaleType
    );
  }, [customerFilteredSales, selectedSaleType]);

  const filteredSaleIds = useMemo(
    () => new Set(filteredSales.map((s) => s.id)),
    [filteredSales]
  );

  // When a customer is selected, only show payments for that customer's sales
  const filteredPayments = useMemo(() => {
    if (!selectedCustomer) return payments;
    return payments.filter((p) => filteredSaleIds.has(p.sale));
  }, [payments, selectedCustomer, filteredSaleIds]);

  // When a customer is selected, only show returns for that customer's sales
  const filteredReturns = useMemo(() => {
    if (!selectedCustomer) return returns;
    return returns.filter((r) => filteredSaleIds.has(r.sale));
  }, [returns, selectedCustomer, filteredSaleIds]);

  const saleTypeBreakdown = useMemo(() => {
    const initial = {
      wholesale: { count: 0, amount: 0 },
      retail: { count: 0, amount: 0 },
      mixed: { count: 0, amount: 0 },
    };

    customerFilteredSales.forEach((sale) => {
      const type = getSaleType(sale);
      initial[type].count += 1;
      initial[type].amount += getSaleTotal(sale);
    });

    return initial;
  }, [customerFilteredSales]);

  const totalBalancePaymentsAmount = balancePaymentsFiltered.reduce(
    (sum, p) => sum + parseAmount(p.amount),
    0
  );
  const totalSalePaymentsAmount = filteredPayments.reduce(
    (sum, p) => sum + parseAmount(p.amount),
    0
  );
  const totalRevenue = totalSalePaymentsAmount + totalBalancePaymentsAmount;

  const previousBalanceAmount = selectedCustomerObj
    ? parseAmount(selectedCustomerObj.opening_balance)
    : 0;
  const previousBalanceCurrency =
    selectedCustomerObj?.opening_balance_currency_details?.title || "";

  // When "All Customers": total opening balance across all customers (for report and PDF)
  const totalOpeningBalanceAmount = useMemo(() => {
    if (selectedCustomer) return 0;
    return (customers || []).reduce(
      (sum, c) => sum + parseAmount(c.opening_balance),
      0
    );
  }, [customers, selectedCustomer]);
  const totalOpeningBalanceByCurrency = useMemo(() => {
    if (selectedCustomer) return {};
    const byCur = {};
    (customers || []).forEach((c) => {
      const cur = c.opening_balance_currency_details?.title || "AF";
      byCur[cur] = (byCur[cur] || 0) + parseAmount(c.opening_balance);
    });
    return byCur;
  }, [customers, selectedCustomer]);

  const summary = {
    previous_balance: selectedCustomer
      ? previousBalanceAmount
      : totalOpeningBalanceAmount,
    previous_balance_currency: selectedCustomer
      ? previousBalanceCurrency
      : Object.keys(totalOpeningBalanceByCurrency).length === 1
        ? Object.keys(totalOpeningBalanceByCurrency)[0]
        : Object.keys(totalOpeningBalanceByCurrency).length > 1
          ? "Various"
          : "",
    total_opening_balance_by_currency: totalOpeningBalanceByCurrency,
    total_sales: {
      count: filteredSales.length,
      amount: filteredSales.reduce(
        (sum, s) => sum + getSaleSubtotal(s),
        0
      ),
      discount_amount: filteredSales.reduce(
        (sum, s) => sum + getSaleDiscount(s),
        0
      ),
      after_discount_amount: filteredSales.reduce(
        (sum, s) => sum + getSaleTotal(s),
        0
      ),
    },
    total_payments: {
      count: filteredPayments.length,
      amount: totalSalePaymentsAmount,
    },
    total_balance_payments: {
      count: balancePaymentsFiltered.length,
      amount: totalBalancePaymentsAmount,
    },
    total_revenue: totalRevenue,
    total_returns: {
      count: filteredReturns.length,
      amount: filteredReturns.reduce(
        (sum, r) => sum + parseAmount(r.total_refound_amount),
        0
      ),
    },
    total_paid: filteredSales.reduce(
      (sum, s) => sum + parseAmount(s.received_amount || 0),
      0
    ),
    total_remaining: filteredSales.reduce(
      (sum, s) => sum + parseAmount(s.total_remained_amount || 0),
      0
    ),
    outstanding_amount: filteredSales.reduce((sum, s) => {
      const saleAmount = getSaleTotal(s);
      const paidAmount = parseAmount(s.received_amount || 0);
      const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
      const returnAmount = saleReturns.reduce(
        (rSum, r) => rSum + parseAmount(r.total_refound_amount),
        0
      );
      return sum + Math.max(0, saleAmount - paidAmount - returnAmount);
    }, 0),
    // When customer selected: total outstanding = previous + sales outstanding - balance payments (matches customer page)
    // When all customers: outstanding from sales + total opening balance (opening balance included in report)
    outstanding_including_previous: selectedCustomer
      ? Math.max(
          0,
          previousBalanceAmount +
            filteredSales.reduce((sum, s) => {
              const saleAmount = getSaleTotal(s);
              const paidAmount = parseAmount(s.received_amount || 0);
              const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
              const returnAmount = saleReturns.reduce(
                (rSum, r) => rSum + parseAmount(r.total_refound_amount),
                0
              );
              return sum + Math.max(0, saleAmount - paidAmount - returnAmount);
            }, 0) -
            totalBalancePaymentsAmount
        )
      : Math.max(
          0,
          filteredSales.reduce((sum, s) => {
            const saleAmount = getSaleTotal(s);
            const paidAmount = parseAmount(s.received_amount || 0);
            const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
            const returnAmount = saleReturns.reduce(
              (rSum, r) => rSum + parseAmount(r.total_refound_amount),
              0
            );
            return sum + Math.max(0, saleAmount - paidAmount - returnAmount);
          }, 0) + totalOpeningBalanceAmount
        ),
    net_sales:
      filteredSales.reduce((sum, s) => sum + getSaleTotal(s), 0) -
      filteredReturns.reduce(
        (sum, r) => sum + parseAmount(r.total_refound_amount),
        0
      ),
    payment_status: {
      paid: filteredSales.filter((s) => {
        const saleAmount = getSaleTotal(s);
        const paidAmount = parseAmount(s.received_amount || 0);
        const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
        const returnAmount = saleReturns.reduce(
          (rSum, r) => rSum + parseAmount(r.total_refound_amount),
          0
        );
        return saleAmount - paidAmount - returnAmount <= 0;
      }).length,
      partial: filteredSales.filter((s) => {
        const saleAmount = getSaleTotal(s);
        const paidAmount = parseAmount(s.received_amount || 0);
        const saleReturns = filteredReturns.filter((r) => r.sale === s.id);
        const returnAmount = saleReturns.reduce(
          (rSum, r) => rSum + parseAmount(r.total_refound_amount),
          0
        );
        const remaining = saleAmount - paidAmount - returnAmount;
        return remaining > 0 && paidAmount > 0;
      }).length,
      unpaid: filteredSales.filter((s) => parseAmount(s.received_amount || 0) === 0)
        .length,
    },
    is_all_customers: !selectedCustomer,
    sale_type_filter: selectedSaleType,
    sale_type_breakdown: saleTypeBreakdown,
  };

  const displayOutstanding =
    summary.outstanding_including_previous != null
      ? summary.outstanding_including_previous
      : summary.outstanding_amount;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue?.id ? String(newValue.id) : "");
  };

  const exportToPDF = () => {
    const exportData = {
      sales: filteredSales,
      payments: filteredPayments,
      returns: filteredReturns,
      balancePayments: balancePaymentsFiltered,
      summary,
      customers,
      selectedSaleType,
    };
    exportComprehensiveSaleReportToPDF(exportData);
  };

  const exportToExcel = () => {
    const exportData = {
      sales: filteredSales,
      payments: filteredPayments,
      returns: filteredReturns,
      balancePayments: balancePaymentsFiltered,
      summary,
      selectedCustomerName: selectedCustomerObj?.name || "",
      selectedSaleType,
      startDate: startDate?.format("YYYY-MM-DD") || "",
      endDate: endDate?.format("YYYY-MM-DD") || "",
    };
    exportComprehensiveSaleReportToExcel(exportData);
  };

  const exportCustomerReport = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const timestamp = new Date().toLocaleString();
    const customerName = selectedCustomerObj?.name || "All Customers";
    const startLabel = startDate ? startDate.format("YYYY-MM-DD") : "All";
    const endLabel = endDate ? endDate.format("YYYY-MM-DD") : "All";

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Report", pageWidth / 2, 12, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Customer: ${customerName}`, 14, 22);
    doc.text(`Start Date: ${startLabel}`, 14, 28);
    doc.text(`End Date: ${endLabel}`, 14, 34);
    doc.text(`Generated: ${timestamp}`, pageWidth - 14, 22, { align: "right" });

    autoTable(doc, {
      startY: 42,
      head: [["Bill No.", "Date", "Description", "Unit", "Qty", "Currency", "Total"]],
      body: filteredCustomerReportRows.map((row) => [
        row.billNo || "-",
        formatReportDate(row.date),
        row.description || "-",
        getOptionTitle(units, row.unit),
        Number(row.qty || 0).toLocaleString(),
        getOptionTitle(currencies, row.currency),
        Number(row.total || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 2,
        valign: "middle",
      },
      headStyles: {
        fillColor: [53, 93, 49],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 8, right: 8 },
    });

    const totalQty = filteredCustomerReportRows.reduce(
      (sum, row) => sum + parseStoredAmount(row.qty),
      0
    );
    const totalAmount = filteredCustomerReportRows.reduce(
      (sum, row) => sum + parseStoredAmount(row.total),
      0
    );
    const totalPaid = filteredCustomerReportRows.reduce(
      (sum, row) => sum + parseStoredAmount(row.paid),
      0
    );
    const totalRemaining = totalAmount - totalPaid;

    const finalY = doc.lastAutoTable?.finalY || 50;
    autoTable(doc, {
      startY: finalY + 8,
      head: [["Total Qty", "Total Amount", "Total Paid", "Remaining"]],
      body: [[
        totalQty.toLocaleString(),
        totalAmount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        totalPaid.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        totalRemaining.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ]],
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "center",
      },
      headStyles: {
        fillColor: [53, 93, 49],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      bodyStyles: {
        fontStyle: "bold",
        textColor: [53, 93, 49],
      },
      margin: { left: 8, right: 8 },
    });

    doc.save(`Customer_Report_${new Date().toISOString().slice(0, 19).replace(/[:]/g, "-")}.pdf`);
  };

  const exportCustomerReportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const customerName = selectedCustomerObj?.name || "All Customers";
    const dataRows = filteredCustomerReportRows.map((row) => ({
      "Bill No.": row.billNo || "-",
      Date: formatReportDate(row.date),
      Description: row.description || "-",
      Unit: getOptionTitle(units, row.unit),
      Qty: Number(row.qty || 0),
      Currency: getOptionTitle(currencies, row.currency),
      Total: Number(row.total || 0),
      Paid: Number(row.paid || 0),
      Remaining: Number(row.remaining || 0),
    }));

    const summaryRows = [
      { Metric: "Customer", Value: customerName },
      { Metric: "Start Date", Value: startDate ? startDate.format("YYYY-MM-DD") : "All" },
      { Metric: "End Date", Value: endDate ? endDate.format("YYYY-MM-DD") : "All" },
      { Metric: "Records", Value: filteredCustomerReportRows.length },
      {
        Metric: "Total Qty",
        Value: filteredCustomerReportRows.reduce((sum, row) => sum + parseStoredAmount(row.qty), 0),
      },
      {
        Metric: "Total Amount",
        Value: filteredCustomerReportRows.reduce((sum, row) => sum + parseStoredAmount(row.total), 0),
      },
      {
        Metric: "Total Paid",
        Value: filteredCustomerReportRows.reduce((sum, row) => sum + parseStoredAmount(row.paid), 0),
      },
      {
        Metric: "Remaining",
        Value:
          filteredCustomerReportRows.reduce((sum, row) => sum + parseStoredAmount(row.total), 0) -
          filteredCustomerReportRows.reduce((sum, row) => sum + parseStoredAmount(row.paid), 0),
      },
    ];

    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    summarySheet["!cols"] = [{ wch: 20 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    const dataSheet = XLSX.utils.json_to_sheet(dataRows);
    dataSheet["!cols"] = [
      { wch: 12 },
      { wch: 14 },
      { wch: 36 },
      { wch: 12 },
      { wch: 10 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(workbook, dataSheet, "Customer Report");

    XLSX.writeFile(
      workbook,
      `Customer_Report_${new Date().toISOString().slice(0, 19).replace(/[:]/g, "-")}.xlsx`
    );
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedCustomer("");
    setSelectedSaleType("all");
    setCustomerReportPage(0);
  };

  const handleOpenDescription = (event, description) => {
    setDescriptionAnchorEl(event.currentTarget);
    setActiveDescription(description || "-");
  };

  const handleCloseDescription = () => {
    setDescriptionAnchorEl(null);
    setActiveDescription("");
  };

  const descriptionPopover = (
    <Popover
      open={Boolean(descriptionAnchorEl)}
      anchorEl={descriptionAnchorEl}
      onClose={handleCloseDescription}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          maxWidth: 420,
          p: 2,
          borderRadius: 2,
          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
        {activeDescription}
      </Typography>
    </Popover>
  );

  useEffect(() => {
    const handleUpdate = () => setCustomerReportRefreshTick((tick) => tick + 1);
    window.addEventListener("storage", handleUpdate);
    window.addEventListener("agent-items-updated", handleUpdate);
    return () => {
      window.removeEventListener("storage", handleUpdate);
      window.removeEventListener("agent-items-updated", handleUpdate);
    };
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: "100%",
          backgroundColor: isDarkMode ? "#121212" : "transparent",
          color: isDarkMode ? "#f3f4f6" : "inherit",
        }}
      >
        <LinearProgress />
        <Typography sx={{ mt: 2, color: isDarkMode ? "#d1d5db" : "inherit" }}>
          Loading sales data...
        </Typography>
      </Box>
    );
  }

  const surfaceBg = isDarkMode ? "#1a1a1a" : "#fff";
  const surfaceBorder = isDarkMode ? "#2f2f2f" : "#d9e2ec";
  const tableRowBg = isDarkMode ? "#1b1b1b" : "#fff";
  const tableRowHover = isDarkMode ? "#232323" : "#f8fbff";
  const titleColor = isDarkMode ? "#f9fafb" : "#102a43";
  const mutedText = isDarkMode ? "#a9b4c4" : "#5f6f86";
  const tableHeadCellSx = {
    backgroundColor: "#355d31",
    color: "#ffffff",
    fontWeight: 700,
  };
  const totalRowSx = {
    backgroundColor: isDarkMode ? "#243f23" : "#e8f1fb",
    "& .MuiTableCell-root": {
      color: isDarkMode ? "#f3f4f6" : "#102a43",
      fontWeight: 700,
    },
  };

  if (viewMode === "simple") {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          color: isDarkMode ? "#f3f4f6" : "#102a43",
          backgroundColor: isDarkMode ? "#121212" : "transparent",
        }}
      >
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: surfaceBg,
            color: isDarkMode ? "#f3f4f6" : "#102a43",
            border: `1px solid ${surfaceBorder}`,
            borderRadius: 2,
            boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.3)" : undefined,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#355d31" }}>
                Customer Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Saved bill items from the Agent form appear here automatically.
              </Typography>
            </Box>
          </Box>

          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: surfaceBg,
              color: isDarkMode ? "#f3f4f6" : "#102a43",
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.3)" : undefined,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={handleCustomerChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    clearOnBlur={false}
                    renderInput={(params) => <TextField {...params} label="Customer" />}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth sx={{ height: 56 }}>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportCustomerReport}
                    startIcon={<Download />}
                    fullWidth
                    sx={{ height: 56 }}
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportCustomerReportToExcel}
                    startIcon={<Download />}
                    fullWidth
                    sx={{ height: 56 }}
                  >
                    Export Excel
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Paper>

          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: titleColor }}>
              Rows per page
            </Typography>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select
                value={customerReportRowsPerPage}
                onChange={(event) => {
                  const nextValue = Number(event.target.value);
                  setCustomerReportRowsPerPage(nextValue);
                  setCustomerReportPage(0);
                }}
                sx={{
                  height: 40,
                  backgroundColor: surfaceBg,
                  color: isDarkMode ? "#f3f4f6" : "#132238",
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={-1}>All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 2,
              color: titleColor,
              fontWeight: 400,
            }}
          >
            Customer Report Data Table
          </Typography>

          <TableContainer
            sx={{
              border: `1px solid ${surfaceBorder}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Bill No.", "Date", "", "Unit", "Qty", "Currency", "Total"].map(
                    (label) => (
                      <TableCell
                        key={label}
                        sx={{
                          backgroundColor: "#355d31",
                          color: "#fff",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          py: 1.4,
                          px: 1.6,
                        }}
                      >
                        {label}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedCustomerReportRows.length ? (
                  pagedCustomerReportRows.map((row, index) => (
                    <TableRow
                      key={`${row.id}-${customerReportPage}-${index}`}
                      hover
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: isDarkMode ? "#171717" : "#fafcfe",
                        },
                        "& td": {
                          borderColor: surfaceBorder,
                          py: 1.35,
                          px: 1.6,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>{row.billNo}</TableCell>
                      <TableCell>{formatReportDate(row.date)}</TableCell>
                      <TableCell
                        onClick={(event) => handleOpenDescription(event, row.description)}
                        title="View description"
                        sx={{
                          textAlign: "center",
                          width: 72,
                          minWidth: 72,
                          maxWidth: 72,
                          px: 2,
                          py: 2.5,
                          cursor: "pointer",
                        }}
                      />
                      <TableCell>
                        <Chip
                          label={getOptionTitle(units, row.unit)}
                          size="small"
                          sx={{
                            backgroundColor: isDarkMode ? "#243f23" : "#edf6ec",
                            color: isDarkMode ? "#d9f0d9" : "#355d31",
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {Number(row.qty || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>{getOptionTitle(currencies, row.currency)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#1f7a4c" }}>
                        {Number(row.total || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      No customer report records found.
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      borderTop: `1px solid ${surfaceBorder}`,
                      py: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {[
                        ["Qty", customerReportSummary.totalQty, "#2f7d32", "#e8f5e9"],
                        ["Total", customerReportSummary.totalAmount, "#355d31", "#edf6ec"],
                        ["Paid", customerReportSummary.totalPaid, "#2f7d32", "#eef7ee"],
                        ["Remaining", customerReportSummary.remaining, "#d32f2f", "#fdecec"],
                      ].map(([label, value, borderColor, bgColor]) => (
                        <Box
                          key={label}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.6,
                            py: 0.8,
                            borderRadius: 999,
                            border: `1px solid ${borderColor}`,
                            backgroundColor: bgColor,
                            whiteSpace: "nowrap",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            {label}
                          </Box>
                          <Box component="span" sx={{ color: borderColor }}>
                            {Number(value || 0).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {descriptionPopover}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={
                customerReportRowsPerPage === -1
                  ? 1
                  : Math.max(
                      1,
                      Math.ceil(
                        filteredCustomerReportRows.length / customerReportRowsPerPage
                      )
                    )
              }
              page={customerReportRowsPerPage === -1 ? 1 : customerReportPage + 1}
              onChange={(_, nextPage) => setCustomerReportPage(nextPage - 1)}
              shape="rounded"
              color="success"
            />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        color: isDarkMode ? "#f3f4f6" : "#102a43",
        backgroundColor: isDarkMode ? "#121212" : "transparent",
      }}
    >
      <Typography variant="h4" sx={{ mb: 3, color: titleColor, fontWeight: 700 }}>
        Comprehensive Sales Report
      </Typography>

      {/* View Mode Toggle */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant={viewMode === "simple" ? "contained" : "outlined"}
          onClick={() => setViewMode("simple")}
          sx={{
            mr: 1,
            color: viewMode === "simple" ? "#fff" : isDarkMode ? "#d1d5db" : "#355d31",
            borderColor: "#355d31",
            backgroundColor: viewMode === "simple" ? "#355d31" : "transparent",
            "&:hover": {
              backgroundColor: viewMode === "simple" ? "#274923" : "rgba(53, 93, 49, 0.12)",
              borderColor: "#274923",
            },
          }}
        >
          📊 Simple View
        </Button>
        <Button
          variant={viewMode === "detailed" ? "contained" : "outlined"}
          onClick={() => setViewMode("detailed")}
          sx={{
            color: viewMode === "detailed" ? "#fff" : isDarkMode ? "#d1d5db" : "#355d31",
            borderColor: "#355d31",
            backgroundColor: viewMode === "detailed" ? "#355d31" : "transparent",
            "&:hover": {
              backgroundColor: viewMode === "detailed" ? "#274923" : "rgba(53, 93, 49, 0.12)",
              borderColor: "#274923",
            },
          }}
        >
          📋 Detailed View
        </Button>
      </Box>

      {viewMode === "simple" ? (
        /* Simple View */
        <Box>
          {/* Filters for Simple View */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: surfaceBg,
              color: isDarkMode ? "#f3f4f6" : "#102a43",
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.3)" : undefined,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={handleCustomerChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Customer" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportToPDF}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export Excel
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportCustomerReport}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Customer Report
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Paper>

          <Paper
            sx={{
              p: 3,
              mb: 3,
              textAlign: "center",
              backgroundColor: surfaceBg,
              color: isDarkMode ? "#f3f4f6" : "#102a43",
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.3)" : undefined,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: "#355d31", fontWeight: 700 }}>
              Customer Report
            </Typography>
            <TableContainer sx={{ mb: 3, border: `1px solid ${surfaceBorder}`, borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Bill No.</TableCell>
                  <TableCell sx={tableHeadCellSx}>Date</TableCell>
                  <TableCell
                    sx={{
                      ...tableHeadCellSx,
                      width: 72,
                      minWidth: 72,
                      maxWidth: 72,
                    }}
                  />
                    <TableCell sx={tableHeadCellSx}>Unit</TableCell>
                    <TableCell sx={tableHeadCellSx}>Qty</TableCell>
                    <TableCell sx={tableHeadCellSx}>Currency</TableCell>
                    <TableCell sx={tableHeadCellSx}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerReportRows.length ? (
                    customerReportRows.map((row, index) => (
                      <TableRow key={`${row.billNo}-${index}`}>
                        <TableCell>{row.billNo}</TableCell>
                        <TableCell>{formatReportDate(row.date)}</TableCell>
                        <TableCell
                          onClick={(event) => handleOpenDescription(event, row.description)}
                          title="View description"
                          sx={{
                            textAlign: "center",
                            width: 72,
                            minWidth: 72,
                            maxWidth: 72,
                            px: 2,
                            py: 2.5,
                            cursor: "pointer",
                          }}
                        />
                        <TableCell>{row.unit}</TableCell>
                        <TableCell>{Number(row.qty || 0).toLocaleString()}</TableCell>
                        <TableCell>{row.currency}</TableCell>
                        <TableCell>{Number(row.total || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No customer report records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {descriptionPopover}

            <TableContainer sx={{ mb: 3, border: `1px solid ${surfaceBorder}`, borderRadius: 1 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {customerReportSummary.totalAmount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total Paid</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {customerReportSummary.totalPaid.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Remaining</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {customerReportSummary.remaining.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography
              variant="h5"
              sx={{ mb: 2, color: "#355d31", fontWeight: 700 }}
            >
              📈 Quick Overview
            </Typography>
            <Grid container spacing={2}>
              {(selectedCustomer || totalOpeningBalanceAmount > 0) && (
                <Grid item xs={12} md={2.4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#243b53",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      {summary.previous_balance.toFixed(2)}{" "}
                      {summary.previous_balance_currency || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Previous Balance (Opening)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "white" }}>
                      {selectedCustomer
                        ? selectedCustomerObj?.name || "Customer"
                        : "All customers"}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#0f4c81",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_sales.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Total Sales
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_sales.amount.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Discount: {summary.total_sales.discount_amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#1f7a4c",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_revenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Revenue (Collected)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Sale payments + Balance payments
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#8a6d1f",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {displayOutstanding.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Outstanding
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    {selectedCustomer
                      ? "Previous + Sales (after returns)"
                      : "Opening balance + Sales (after returns)"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#5b2a86",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_balance_payments.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Balance Payments
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_balance_payments.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#334e68",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "white", mb: 1 }}>
                    Payment Status
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ✅ Paid: {summary.payment_status.paid}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ⏳ Partial: {summary.payment_status.partial}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ❌ Unpaid: {summary.payment_status.unpaid}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            sx={{
              p: 2,
              backgroundColor: surfaceBg,
              color: isDarkMode ? "#f3f4f6" : "#102a43",
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.3)" : undefined,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: titleColor }}>
              Recent Sales
            </Typography>
            {filteredSales.slice(0, 5).map((sale) => {
              const saleReturns = filteredReturns.filter(
                (r) => r.sale === sale.id
              );
              const returnAmount = saleReturns.reduce(
                (sum, r) => sum + parseAmount(r.total_refound_amount),
                0
              );
              const remaining =
                getSaleTotal(sale) -
                parseAmount(sale.received_amount || 0) -
                returnAmount;

              return (
                <Box
                  key={sale.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: 1,
                    bgcolor: isDarkMode ? "#232323" : "grey.50",
                    borderRadius: 1,
                    border: `1px solid ${isDarkMode ? "#2f2f2f" : "#e5e7eb"}`,
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      #{sale.id} -{" "}
                      {sale.customer_details?.name || "Walk-in Customer"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText }}>
                      {sale.sale_date}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="h6" sx={{ color: "#355d31" }}>
                      {getSaleSubtotal(sale).toFixed(2)}
                    </Typography>
                    <Chip
                      label={remaining <= 0 ? "✅ Paid" : "⏳ Pending"}
                      size="small"
                      color={remaining <= 0 ? "success" : "warning"}
                    />
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Box>
      ) : (
        /* Detailed View */
        <Box>
          {/* Comprehensive Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {selectedCustomer && (
              <Grid item xs={12} md={2}>
                <Card sx={{ bgcolor: isDarkMode ? "#243b53" : "grey.700" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ color: "white" }}>
                      Previous Balance (Opening)
                    </Typography>
                    <Typography variant="h4" sx={{ color: "white" }}>
                      {summary.previous_balance.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {summary.previous_balance_currency || "—"} •{" "}
                      {selectedCustomerObj?.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: isDarkMode ? "#0f4c81" : "primary.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="primary">
                    Total Sales
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {summary.total_sales.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_sales.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: isDarkMode ? "#1f7a4c" : "success.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="success.main">
                    Revenue
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {summary.total_revenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    Sale + Balance payments
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: isDarkMode ? "#8a6d1f" : "warning.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="warning.main">
                    Outstanding
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {displayOutstanding.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    {selectedCustomer ? "Previous + Sales" : "After Returns"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: isDarkMode ? "#5b2a86" : "secondary.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="secondary.main">
                    Balance Payments
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {summary.total_balance_payments.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_balance_payments.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: isDarkMode ? "#334e68" : "grey.200" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="text.secondary">
                    Net Sales
                  </Typography>
                  <Typography variant="h4" color="text.secondary">
                    {summary.net_sales.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">After Returns</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Financial Summary */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              bgcolor: surfaceBg,
              color: isDarkMode ? "#f3f4f6" : "#102a43",
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.3)" : undefined,
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: "center", color: "#355d31", fontWeight: 700 }}
            >
              💰 Complete Financial Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "#355d31",
                    borderRadius: 2,
                    bgcolor: isDarkMode ? "#1f1f1f" : "#fff",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1, color: "#355d31" }}>
                    Sales Summary
                  </Typography>
                  {selectedCustomer && (
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      Previous Balance (Opening):{" "}
                      <strong>
                        {summary.previous_balance.toFixed(2)}{" "}
                        {summary.previous_balance_currency || ""}
                      </strong>
                    </Typography>
                  )}
                  <Typography variant="body1">
                    Invoice Subtotal:{" "}
                    <strong>{summary.total_sales.amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Discount Amount:{" "}
                    <strong>{summary.total_sales.discount_amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Paid Amount:{" "}
                    <strong>{summary.total_paid.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Outstanding Amount:{" "}
                    <strong>{displayOutstanding.toFixed(2)}</strong>
                  </Typography>
                  {selectedCustomer && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      (Previous {summary.previous_balance.toFixed(2)} + Sales outstanding {summary.outstanding_amount.toFixed(2)})
                    </Typography>
                  )}
                  {selectedCustomer &&
                    selectedCustomerObj?.total_owed_by_currency &&
                    typeof selectedCustomerObj.total_owed_by_currency ===
                      "object" && (
                      <Typography
                        variant="body1"
                        sx={{ mt: 1, fontWeight: "bold" }}
                      >
                        Total Owed (customer):{" "}
                        {Object.entries(
                          selectedCustomerObj.total_owed_by_currency
                        )
                          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
                          .join(", ") || "—"}
                      </Typography>
                    )}
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: mutedText }}
                  >
                    Collection Progress:{" "}
                    {summary.total_sales.after_discount_amount > 0
                      ? (
                          (summary.total_paid /
                            summary.total_sales.after_discount_amount) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "#1f7a4c",
                    borderRadius: 2,
                    bgcolor: isDarkMode ? "#1f1f1f" : "#fff",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1, color: "#1f7a4c" }}>
                    Revenue (Collected)
                  </Typography>
                  <Typography variant="body1">
                    Sale Payments:{" "}
                    <strong>{summary.total_payments.amount.toFixed(2)}</strong>{" "}
                    ({summary.total_payments.count})
                  </Typography>
                  <Typography variant="body1">
                    Balance Payments:{" "}
                    <strong>
                      {summary.total_balance_payments.amount.toFixed(2)}
                    </strong>{" "}
                    ({summary.total_balance_payments.count})
                  </Typography>
                  <Typography variant="body1">
                    Total Revenue:{" "}
                    <strong>{summary.total_revenue.toFixed(2)}</strong>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "#4d89a6",
                    borderRadius: 2,
                    bgcolor: isDarkMode ? "#1f1f1f" : "#fff",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1, color: "#4d89a6" }}>
                    Returns & Net
                  </Typography>
                  <Typography variant="body1">
                    Total Returns:{" "}
                    <strong>{summary.total_returns.amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Net Sales Value:{" "}
                    <strong>{summary.net_sales.toFixed(2)}</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: mutedText }}
                  >
                    Note: Returns are deducted from outstanding amount for
                    accurate balance calculation
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={handleCustomerChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Customer" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportToPDF}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export Excel
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          {/* Tabs */}
          <Paper sx={{ width: "100%" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`Sales (${filteredSales.length})`} />
              <Tab label={`Sale Payments (${filteredPayments.length})`} />
              <Tab label={`Returns (${filteredReturns.length})`} />
              <Tab
                label={`Balance Payments (${balancePaymentsFiltered.length})`}
              />
            </Tabs>

            {/* Sales Tab */}
            <TabPanel value={tabValue} index={0}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Customer</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Type</TableCell>
                      <TableCell sx={tableHeadCellSx}>Total Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Discount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Paid</TableCell>
                      <TableCell sx={tableHeadCellSx}>Outstanding</TableCell>
                      <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSales.map((sale) => {
                      const saleReturns = filteredReturns.filter(
                        (r) => r.sale === sale.id
                      );
                      const returnAmount = saleReturns.reduce(
                        (sum, r) => sum + parseAmount(r.total_refound_amount),
                        0
                      );
                      const paidAmount = parseAmount(sale.received_amount || 0);
                      const totalAmount = getSaleSubtotal(sale);
                      const discountAmount = getSaleDiscount(sale);
                      const amountAfterDiscount = getSaleTotal(sale);
                      const outstanding = Math.max(
                        0,
                        amountAfterDiscount - paidAmount - returnAmount
                      );
                      const status =
                        outstanding <= 0
                          ? "Paid"
                          : paidAmount > 0
                          ? "Partial"
                          : "Unpaid";
                      const statusColor =
                        outstanding <= 0
                          ? "success"
                          : paidAmount > 0
                          ? "warning"
                          : "error";

                      return (
                        <TableRow key={sale.id}>
                          <TableCell>#{sale.id}</TableCell>
                          <TableCell>
                            {sale.customer_details?.name || "Walk-in Customer"}
                          </TableCell>
                          <TableCell>{sale.sale_date}</TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {getSaleType(sale)}
                          </TableCell>
                          <TableCell>{totalAmount.toFixed(2)}</TableCell>
                          <TableCell>{discountAmount.toFixed(2)}</TableCell>
                          <TableCell>{paidAmount.toFixed(2)}</TableCell>
                          <TableCell>{outstanding.toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={status}
                              size="small"
                              color={statusColor}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={4}>
                        <strong>TOTAL ({filteredSales.length} sales)</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_sales.amount.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_sales.discount_amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_paid.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{displayOutstanding.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>-</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Payments Tab */}
            <TabPanel value={tabValue} index={1}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sale</TableCell>
                      <TableCell sx={tableHeadCellSx}>Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Method</TableCell>
                      <TableCell sx={tableHeadCellSx}>Currency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>#{payment.id}</TableCell>
                        <TableCell>Sale #{payment.sale}</TableCell>
                        <TableCell>
                          {parseAmount(payment.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>{payment.payment_date}</TableCell>
                        <TableCell>
                          {payment.payment_method || "Cash"}
                        </TableCell>
                        <TableCell>
                          {payment.currency_details?.title || "USD"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={2}>
                        <strong>
                          TOTAL ({filteredPayments.length} payments)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_payments.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell colSpan={3}>
                        <strong>Total Collected</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Returns Tab */}
            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sale</TableCell>
                      <TableCell sx={tableHeadCellSx}>Return Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Reason</TableCell>
                      <TableCell sx={tableHeadCellSx}>Refund Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReturns.map((returnItem) => (
                      <TableRow key={returnItem.id}>
                        <TableCell>#{returnItem.id}</TableCell>
                        <TableCell>Sale #{returnItem.sale}</TableCell>
                        <TableCell>{returnItem.return_date}</TableCell>
                        <TableCell>
                          {returnItem.reason || "No reason provided"}
                        </TableCell>
                        <TableCell>
                          {parseAmount(returnItem.total_refound_amount).toFixed(
                            2
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={returnItem.return_items?.length || 0}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={4}>
                        <strong>
                          TOTAL ({filteredReturns.length} returns)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_returns.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {filteredReturns.reduce(
                            (sum, r) => sum + (r.return_items?.length || 0),
                            0
                          )}{" "}
                          items returned
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Balance Payments Tab */}
            <TabPanel value={tabValue} index={3}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Customer</TableCell>
                      <TableCell sx={tableHeadCellSx}>Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Currency</TableCell>
                      <TableCell sx={tableHeadCellSx}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balancePaymentsFiltered.map((bp) => (
                      <TableRow key={bp.id}>
                        <TableCell>#{bp.id}</TableCell>
                        <TableCell>
                          {bp.customer_details?.name || "-"}
                        </TableCell>
                        <TableCell>
                          {parseAmount(bp.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {bp.payment_date
                            ? new Date(bp.payment_date).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {bp.currency_details?.title || "-"}
                        </TableCell>
                        <TableCell>{bp.notes || "-"}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={2}>
                        <strong>
                          TOTAL ({balancePaymentsFiltered.length} balance
                          payments)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_balance_payments.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell colSpan={3}>
                        <strong>Total collected (balance)</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ComprehensiveSaleReport;

